import type { JsonValue } from "@openfeature/core";
import * as z from "zod";
import type { FlagContextType } from "./context";

export enum FeatureFlagType {
  BOOLEAN = "boolean",
  STRING = "string",
  NUMBER = "number",
  JSON = "json",
}

/**
 * A flag evaluated for a context of type `C`. Evaluation parses the provider's
 * value with `schema`, falling back to `defaultValue`.
 */
export type FeatureFlag<C, T> = {
  readonly key: string;
  readonly context: FlagContextType<C>;
  readonly schema: z.ZodType<T>;
  readonly defaultValue: T;
} & (
  | {
      readonly type: FeatureFlagType.BOOLEAN;
      readonly rawDefaultValue: boolean;
    }
  | { readonly type: FeatureFlagType.STRING; readonly rawDefaultValue: string }
  | { readonly type: FeatureFlagType.NUMBER; readonly rawDefaultValue: number }
  | { readonly type: FeatureFlagType.JSON; readonly rawDefaultValue: JsonValue }
);

interface FeatureFlagBuilder<C> {
  boolean(defaultValue: boolean): FeatureFlag<C, boolean>;
  string(defaultValue: string): FeatureFlag<C, string>;
  string<T>(
    defaultValue: string,
    schema: z.ZodType<T, string>,
  ): FeatureFlag<C, T>;
  number(defaultValue: number): FeatureFlag<C, number>;
  number<T>(
    defaultValue: number,
    schema: z.ZodType<T, number>,
  ): FeatureFlag<C, T>;
  json<T>(defaultValue: JsonValue, schema: z.ZodType<T>): FeatureFlag<C, T>;
}

/**
 * Defines a flag next to the code that evaluates it, e.g.
 * `featureFlag("my-flag", teamContext).boolean(false)`. Throws if the default fails the schema.
 */
export function featureFlag<C>(
  key: string,
  context: FlagContextType<C>,
): FeatureFlagBuilder<C> {
  function string(defaultValue: string): FeatureFlag<C, string>;
  function string<T>(
    defaultValue: string,
    schema: z.ZodType<T, string>,
  ): FeatureFlag<C, T>;
  function string(
    rawDefaultValue: string,
    schema: z.ZodType<unknown, string> = z.string(),
  ): FeatureFlag<C, unknown> {
    return {
      key,
      context,
      schema,
      type: FeatureFlagType.STRING,
      rawDefaultValue,
      defaultValue: schema.parse(rawDefaultValue),
    };
  }

  function number(defaultValue: number): FeatureFlag<C, number>;
  function number<T>(
    defaultValue: number,
    schema: z.ZodType<T, number>,
  ): FeatureFlag<C, T>;
  function number(
    rawDefaultValue: number,
    schema: z.ZodType<unknown, number> = z.number(),
  ): FeatureFlag<C, unknown> {
    return {
      key,
      context,
      schema,
      type: FeatureFlagType.NUMBER,
      rawDefaultValue,
      defaultValue: schema.parse(rawDefaultValue),
    };
  }

  return {
    boolean: (rawDefaultValue) => ({
      key,
      context,
      schema: z.boolean(),
      type: FeatureFlagType.BOOLEAN,
      rawDefaultValue,
      defaultValue: rawDefaultValue,
    }),
    string,
    number,
    json: (rawDefaultValue, schema) => ({
      key,
      context,
      schema,
      type: FeatureFlagType.JSON,
      rawDefaultValue,
      defaultValue: schema.parse(rawDefaultValue),
    }),
  };
}
